import PasswordField from "../common/PasswordField";
import {
  Avatar,
  Box,
  Button,
  TextField,
  Typography,
  Snackbar,
  Alert,
  CircularProgress,
  MenuItem,
} from "@mui/material";
import { Formik } from "formik";
import * as yup from "yup";
import useMediaQuery from "@mui/material/useMediaQuery";
import { theme } from "../../theme";
import ImageUploading, { ImageListType } from "react-images-uploading";
import React, { useState, useEffect } from "react";
import DeleteIcon from "@mui/icons-material/Delete";
import CameraIcon from "@mui/icons-material/CameraAlt";
import UserService from "../../services/UserService";
import IUser from "../../entities/IUser";
import CountrySelect from "../common/CountrySelect";
import { useParams, useNavigate } from "react-router-dom";
import { getErrorMessage, unwrapData } from "../../services/apiUtils";

const GENDER_OPTIONS = ["male", "female", "other"];

const ROLE_OPTIONS: Array<{ id: number; label: string; roleName: string }> = [
  { id: 1, label: "Admin", roleName: "ADMIN" },
  { id: 2, label: "End User", roleName: "USER" },
];

const STATUS_OPTIONS: Array<{ id: number; label: string }> = [
  { id: 1, label: "Active" },
  { id: 0, label: "Inactive" },
];

const toDateInputValue = (dob: any): string => {
  if (!dob) return "";
  if (Array.isArray(dob) && dob.length >= 3) {
    const [y, m, d] = dob;
    return `${String(y).padStart(4, "0")}-${String(m).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
  }
  if (typeof dob === "string") return dob.split("T")[0];
  return "";
};

const toDobArray = (value: string): [number, number, number] | null => {
  if (!value) return null;
  const [y, m, d] = value.split("-").map((v) => Number(v));
  if (!y || !m || !d) return null;
  return [y, m, d];
};

interface UserFormValues {
  userId: number;
  name: string;
  username: string;
  passcode: string;
  firstName: string;
  lastName: string;
  emailId: string;
  mobile: string;
  gender: string;
  dob: string;
  roleId: number;
  statusId: number;
  country: string;

  addressId: number;
  addressName: string;
  address1: string;
  address2: string;
  cityName: string;
  stateName: string;
  postCode: string;
  addressPhone: string;
}

const emptyUser: UserFormValues = {
  userId: 0,
  name: "",
  username: "",
  passcode: "",
  firstName: "",
  lastName: "",
  emailId: "",
  mobile: "",
  gender: "",
  dob: "",
  roleId: 2,
  statusId: 1,
  country: "PK",
  addressId: 0,
  addressName: "",
  address1: "",
  address2: "",
  cityName: "",
  stateName: "",
  postCode: "",
  addressPhone: "",
};

const getPrimaryAddress = (data: IUser) => {
  const list = data?.addressList || [];
  return list.find((a) => a.isDefaultAddress) || list[0] || null;
};

const UserForm: React.FC = (props: any) => {
  const [userImage, setUserImage] = React.useState([]);
  const isNonMobile = useMediaQuery("(min-width:600px)");

  const { id } = useParams();
  let navigate = useNavigate();

  const userId = Number(id) || 0;
  const isEditMode = userId > 0;

  const [initialValues, setInitialValues] = useState<UserFormValues>(emptyUser);
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });

  useEffect(() => {
    if (!isEditMode) {
      setInitialValues(emptyUser);
      setIsLoading(false);
      return;
    }
    setIsLoading(true);
    UserService.getUserById(userId)
      .then((res: any) => {
        const data = unwrapData(res) as IUser;
        const genderFromApi = (data?.gender || "").toLowerCase();
        const primaryAddress = getPrimaryAddress(data);
        const matchedRole = ROLE_OPTIONS.find(
          (r) => r.id === data?.userRole?.userRoleId || r.roleName === data?.userRole?.roleName
        );
        setInitialValues({
          userId: data?.userId ?? userId,
          name: data?.name ?? "",
          username: data?.username ?? "",
          passcode: data?.passcode ?? "",
          firstName: data?.firstName ?? "",
          lastName: data?.lastName ?? "",
          emailId: data?.emailId ?? "",
          mobile: data?.mobile ?? "",
          gender: GENDER_OPTIONS.includes(genderFromApi) ? genderFromApi : "",
          dob: toDateInputValue(data?.dob),
          roleId: matchedRole?.id ?? 2,
          statusId:
            data?.statusId != null ? data.statusId : data?.enabled === false ? 0 : 1,
          country: data?.country || "PK",
          addressId: primaryAddress?.addressId ?? 0,
          addressName: primaryAddress?.addressName ?? "",
          address1: primaryAddress?.address1 ?? "",
          address2: primaryAddress?.address2 ?? "",
          cityName: primaryAddress?.cityName ?? "",
          stateName: primaryAddress?.stateName ?? "",
          postCode: primaryAddress?.postCode ?? "",
          addressPhone: primaryAddress?.phone ?? "",
        });
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(err, "Could not load this user."),
          severity: "error",
        });
      })
      .finally(() => setIsLoading(false));
  }, [userId, isEditMode]);

  const handleFormSubmit = (values: UserFormValues, { setSubmitting }: any) => {
    const selectedRole = ROLE_OPTIONS.find((r) => r.id === values.roleId) || ROLE_OPTIONS[1];
    const fullName =
      values.name.trim() || [values.firstName, values.lastName].filter(Boolean).join(" ").trim();

    const payload: any = {
      username: values.username,
      name: fullName,
      firstName: values.firstName,
      lastName: values.lastName,
      emailId: values.emailId,
      mobile: values.mobile,
      passcode: values.passcode,
      gender: values.gender || "female",
      dob: toDobArray(values.dob),
      type: 1,
      provider: "LOCAL",
      statusId: values.statusId,
      enabled: values.statusId === 1,
      country: values.country,
      userRole: { userRoleId: selectedRole.id, roleName: selectedRole.roleName },
    };
    if (isEditMode) payload.userId = userId;

    const request = isEditMode
      ? UserService.updateUser(userId, payload)
      : UserService.createUser(payload);

    request
      .then(() => {
        setSnackbar({
          open: true,
          message: isEditMode ? "User updated successfully." : "User created successfully.",
          severity: "success",
        });
        setTimeout(() => navigate("/users"), 600);
      })
      .catch((err: any) => {
        setSnackbar({
          open: true,
          message: getErrorMessage(
            err,
            isEditMode ? "Could not update the user. Please try again." : "Could not create the user. Please try again."
          ),
          severity: "error",
        });
      })
      .finally(() => setSubmitting(false));
  };

  const onUserImgChangeHandler = (
    imageList: ImageListType,
    addUpdateIndex: number[] | undefined
  ) => {
    setUserImage(imageList as never[]);
  };

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Formik
        enableReinitialize
        onSubmit={handleFormSubmit}
        initialValues={initialValues}
        validationSchema={checkoutSchema}
      >
        {({
          values,
          errors,
          touched,
          handleBlur,
          handleChange,
          handleSubmit,
          setFieldValue,
          isSubmitting,
        }) => (
          <form onSubmit={handleSubmit}>
            <Box display="flex" gap="30px" flexWrap="wrap">
              <Box flex="1 1 560px" display="flex" flexDirection="column" gap="24px">
                <Box
                  display="grid"
                  gap="20px"
                  gridTemplateColumns={isNonMobile ? "repeat(2, 1fr)" : "1fr"}
                  sx={{
                    backgroundColor: "white",
                    borderRadius: "12px",
                    boxShadow:
                      "0px 2px 1px -1px rgb(0 0 0 / 20%), 0px 1px 1px 0px rgb(0 0 0 / 14%), 0px 1px 3px 0px rgb(0 0 0 / 12%)",
                    p: 2,
                  }}
                >
                  <Typography
                    gridColumn="1/-1"
                    borderBottom={`3px solid ${theme.palette.primary.main}`}
                    variant="h6"
                    color="text.secondary"
                    sx={{ m: "0 0 4px 0" }}
                  >
                    Basic Info
                  </Typography>

                  <TextField
                    fullWidth
                    type="text"
                    label="Name"
                    value={values.name}
                    name="name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText="Optional — defaults to First + Last name if left blank"
                  />
                  <TextField
                    fullWidth
                    type="text"
                    label="Username"
                    value={values.username}
                    name="username"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!touched.username && !!errors.username}
                    helperText={touched.username && errors.username}
                  />

                  <PasswordField
                    fullWidth
                    label="Passcode"
                    value={values.passcode}
                    name="passcode"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!touched.passcode && !!errors.passcode}
                    helperText={touched.passcode && errors.passcode}
                  />
                  <TextField
                    fullWidth
                    type="email"
                    label="Email"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.emailId}
                    name="emailId"
                    error={!!touched.emailId && !!errors.emailId}
                    helperText={touched.emailId && errors.emailId}
                  />

                  <TextField
                    fullWidth
                    type="text"
                    label="First Name"
                    value={values.firstName}
                    name="firstName"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!touched.firstName && !!errors.firstName}
                    helperText={touched.firstName && errors.firstName}
                  />
                  <TextField
                    fullWidth
                    type="text"
                    label="Last Name"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.lastName}
                    name="lastName"
                    error={!!touched.lastName && !!errors.lastName}
                    helperText={touched.lastName && errors.lastName}
                  />

                  <TextField
                    fullWidth
                    type="text"
                    label="Mobile"
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.mobile}
                    name="mobile"
                    error={!!touched.mobile && !!errors.mobile}
                    helperText={touched.mobile && errors.mobile}
                  />
                  <TextField
                    fullWidth
                    select
                    label="Gender"
                    name="gender"
                    value={values.gender}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    error={!!touched.gender && !!errors.gender}
                    helperText={touched.gender && errors.gender}
                  >
                    {GENDER_OPTIONS.map((g) => (
                      <MenuItem key={g} value={g}>
                        {g.charAt(0).toUpperCase() + g.slice(1)}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    type="date"
                    label="Date of Birth"
                    name="dob"
                    InputLabelProps={{ shrink: true }}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    value={values.dob}
                    error={!!touched.dob && !!errors.dob}
                    helperText={touched.dob && errors.dob}
                  />
                  <CountrySelect
                    value={values.country}
                    onChange={(code) => setFieldValue("country", code)}
                  />

                  <TextField
                    fullWidth
                    select
                    label="Role"
                    name="roleId"
                    value={values.roleId}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText="Admin → id 1, End User → id 2"
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <MenuItem key={r.id} value={r.id}>
                        {r.label}
                      </MenuItem>
                    ))}
                  </TextField>
                  <TextField
                    fullWidth
                    select
                    label="Status"
                    name="statusId"
                    value={values.statusId}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    helperText="Active → statusId 1, Inactive → statusId 0"
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <MenuItem key={s.id} value={s.id}>
                        {s.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              </Box>

              <Box flex="0 0 260px">
                <Typography
                  borderBottom={`3px solid ${theme.palette.primary.main}`}
                  variant="h6"
                  color="text.secondary"
                  sx={{ m: "0 0 10px 0" }}
                >
                  Profile Image
                </Typography>
                <ImageUploading value={userImage} onChange={onUserImgChangeHandler}>
                  {({ imageList, onImageUpload, onImageRemoveAll, isDragging, dragProps }) => (
                    <Box {...dragProps} sx={{ backgroundColor: isDragging ? "pink" : "white" }}>
                      {imageList.map((image, index) => (
                        <Avatar
                          key={index}
                          onClick={onImageUpload}
                          alt="User avatar"
                          src={image.dataURL}
                          sx={{ margin: "0 auto", width: 240, height: 240 }}
                        />
                      ))}

                      <Box textAlign="center">
                        {imageList.length > 0 && (
                          <Typography
                            variant="subtitle1"
                            color="text.secondary"
                            sx={{ fontStyle: "italic", m: "5px" }}
                          >
                            Click the image to edit or update
                          </Typography>
                        )}
                        <Box
                          height={imageList.length > 0 ? "auto" : "350px"}
                          sx={{
                            display: "flex",
                            justifyContent: "space-around",
                            alignItems: "center",
                          }}
                        >
                          <Button onClick={onImageUpload} variant="contained" endIcon={<CameraIcon />}>
                            Upload
                          </Button>
                          {imageList.length > 0 && (
                            <Button
                              onClick={onImageRemoveAll}
                              variant="outlined"
                              color="error"
                              startIcon={<DeleteIcon />}
                            >
                              Remove
                            </Button>
                          )}
                        </Box>
                      </Box>
                    </Box>
                  )}
                </ImageUploading>
              </Box>
            </Box>
            <Box display="flex" justifyContent="end" mt="20px">
              <Button type="submit" color="secondary" variant="contained" disabled={isSubmitting}>
                {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Create"}
              </Button>
            </Box>
          </form>
        )}
      </Formik>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

const phoneRegExp =
  /^((\+[1-9]{1,4}[ -]?)|(\([0-9]{2,3}\)[ -]?)|([0-9]{2,4})[ -]?)*?[0-9]{3,4}[ -]?[0-9]{3,4}$/;

const checkoutSchema = yup.object().shape({
  username: yup.string().required("required"),
  passcode: yup.string().required("required"),
  firstName: yup.string().required("required"),
  lastName: yup.string().required("required"),
  emailId: yup.string().email("invalid email").required("required"),
  mobile: yup.string().matches(phoneRegExp, "Phone number is not valid").required("required"),
  gender: yup.string().required("required"),
  dob: yup.string().required("required"),
});

export default UserForm;
